// src/Components/Health.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  HealthAndSafety as HealthIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Warning as AllergyIcon,
  CalendarToday as AppointmentIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as HeartIcon,
  LocationOn as LocationIcon,
  OpenInNew as MapIcon,
  Directions as DirectionsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { format, parseISO } from 'date-fns';
import LocationPicker, { buildMapsUrl, buildDirectionsUrl } from './LocationPicker';
import PrivacyControl from './PrivacyControl';
import TagMembers from './TagMembers';
import EmojiPicker from './EmojiPicker';
import { notifyTaggedMembers, notifyHealthEmergency } from '../services/notificationService';
import logger from '../utils/logger';

const Health = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [healthUpdates, setHealthUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingUpdate, setViewingUpdate] = useState(null);
  const [formData, setFormData] = useState({
    memberName: '',
    category: 'general',
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    provider: '',
    dosage: '',
    frequency: '',
    severity: 'moderate',
    followUpDate: '',
    instructions: '',
    isEmergency: false,
    prescriptionNumber: '',
    location: null, // { name, address, lat, lng }
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  });
  const db = getFirestore();

  const categories = [
    { value: 'general', label: 'General Update', icon: <HealthIcon />, color: '#2e7d32' },
    { value: 'doctor', label: 'Doctor Visit', icon: <HospitalIcon />, color: '#0288d1' },
    { value: 'medication', label: 'Medication', icon: <MedicationIcon />, color: '#7b1fa2' },
    { value: 'allergy', label: 'Allergy', icon: <AllergyIcon />, color: '#f57c00' },
    { value: 'appointment', label: 'Appointment', icon: <AppointmentIcon />, color: '#388e3c' }
  ];


  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'healthUpdates'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const updatesData = [];
        querySnapshot.forEach((doc) => {
          updatesData.push({ id: doc.id, ...doc.data() });
        });
        setHealthUpdates(updatesData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching health updates:', error);
        setError('Failed to load health updates');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  const handleSubmit = async () => {
    if (!formData.memberName.trim() || !formData.title.trim()) {
      setError('Member name and title are required');
      return;
    }

    // Validate privacy settings
    if (formData.privacy.visibility === 'private' &&
        formData.privacy.memberIds.length === 0 &&
        formData.privacy.groupTypes.length === 0) {
      setError('Please select at least one member or group for private health updates');
      return;
    }

    try {
      const updateData = {
        memberName: formData.memberName,
        category: formData.category,
        title: formData.title,
        description: formData.description,
        date: formData.date,
        provider: formData.provider,
        dosage: formData.dosage,
        frequency: formData.frequency,
        severity: formData.severity,
        followUpDate: formData.followUpDate,
        instructions: formData.instructions,
        isEmergency: formData.isEmergency,
        prescriptionNumber: formData.prescriptionNumber,
        location: formData.location || null,
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        authorId: currentUser.uid,
        authorName: currentUser.displayName || profile?.displayName || 'Anonymous',
        createdAt: serverTimestamp()
      };

      const createdByName = currentUser.displayName || profile?.displayName || 'Anonymous';
      let updateId = editingUpdate?.id;
      if (editingUpdate) {
        await updateDoc(doc(db, 'families', familyBoardId, 'healthUpdates', editingUpdate.id), {
          ...updateData,
          updatedAt: serverTimestamp()
        });
        setSuccess('Health update saved');

        // Notify newly tagged members on edit
        const previousTagged = editingUpdate.taggedMembers || [];
        const newTagged = (formData.taggedMembers || []).filter(id => !previousTagged.includes(id));
        if (newTagged.length > 0) {
          await notifyTaggedMembers(familyBoardId, newTagged, {
            type: 'health',
            id: editingUpdate.id,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName,
            actionUrl: '/health'
          });
        }
      } else {
        const docRef = await addDoc(collection(db, 'families', familyBoardId, 'healthUpdates'), updateData);
        updateId = docRef.id;
        setSuccess('Health update added');

        if (formData.isEmergency) {
          // Emergency flag — notify entire family immediately
          await notifyHealthEmergency(
            familyBoardId,
            { id: updateId, title: formData.title, memberName: formData.memberName },
            currentUser.uid,
            createdByName
          );
        } else if (formData.taggedMembers && formData.taggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, formData.taggedMembers, {
            type: 'health',
            id: updateId,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName,
            actionUrl: '/health'
          });
        }
      }

      setDialogOpen(false);
      setEditingUpdate(null);
      setFormData({
        memberName: '',
        category: 'general',
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        provider: '',
        dosage: '',
        frequency: '',
        severity: 'moderate',
        followUpDate: '',
        instructions: '',
        isEmergency: false,
        prescriptionNumber: '',
        privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
        taggedMembers: []
      });
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error saving health update:', error);
      setError('Failed to save health update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this health update?')) return;

    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'healthUpdates', id));
      setSuccess('Health update deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting health update:', error);
      setError('Failed to delete health update');
    }
  };

  const openEditDialog = (update) => {
    setEditingUpdate(update);
    setFormData({
      memberName: update.memberName,
      category: update.category,
      title: update.title,
      description: update.description || '',
      date: update.date,
      provider: update.provider || '',
      dosage: update.dosage || '',
      frequency: update.frequency || '',
      severity: update.severity || 'moderate',
      followUpDate: update.followUpDate || '',
      instructions: update.instructions || '',
      isEmergency: update.isEmergency || false,
      prescriptionNumber: update.prescriptionNumber || '',
      privacy: update.privacy || { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: update.taggedMembers || [],
      _previousTaggedMembers: update.taggedMembers || []
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingUpdate(null);
    setFormData({
      memberName: '',
      category: 'general',
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      provider: '',
      dosage: '',
      frequency: '',
      severity: 'moderate',
      followUpDate: '',
      instructions: '',
      isEmergency: false,
      prescriptionNumber: '',
      privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: []
    });
    setDialogOpen(true);
  };

  const openViewDialog = (update) => {
    setViewingUpdate(update);
    setViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setViewingUpdate(null);
  };

  const getCategoryDetails = (categoryValue) => {
    return categories.find(cat => cat.value === categoryValue) || categories[0];
  };

  // Helper function to check if user can view an update based on privacy settings
  const canViewUpdate = (update) => {
    // Update author can always see their own updates
    if (update.authorId === currentUser?.uid) {
      return true;
    }

    // If no privacy settings or public, everyone can see
    if (!update.privacy || update.privacy.visibility === 'public') {
      return true;
    }

    // For private updates, check if user is in the allowed list
    if (update.privacy.visibility === 'private') {
      // Check if user ID is in memberIds
      if (update.privacy.memberIds && update.privacy.memberIds.includes(currentUser?.uid)) {
        return true;
      }

      // Check if user belongs to any of the allowed groups
      if (update.privacy.groupTypes && update.privacy.groupTypes.length > 0 && profile?.groups) {
        const userGroups = profile.groups || [];
        const hasGroupAccess = update.privacy.groupTypes.some(groupType =>
          userGroups.includes(groupType)
        );
        if (hasGroupAccess) {
          return true;
        }
      }

      // User doesn't have access to this private update
      return false;
    }

    return true;
  };

  // Filter updates based on privacy settings
  const visibleUpdates = healthUpdates.filter(update => canViewUpdate(update));

  // Group updates by member
  const updatesByMember = visibleUpdates.reduce((acc, update) => {
    if (!acc[update.memberName]) {
      acc[update.memberName] = [];
    }
    acc[update.memberName].push(update);
    return acc;
  }, {});

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Family Health Updates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track important health information for family members
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add Update
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {healthUpdates.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <HeartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No health updates yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Keep track of doctor visits, medications, and important health information
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add First Update
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {Object.entries(updatesByMember).map(([memberName, updates]) => (
            <Grid item xs={12} md={6} key={memberName}>
              <Card sx={{
                height: '100%',
                '&:hover': { boxShadow: 6 },
                transition: 'all 0.3s',
                border: '2px solid',
                borderColor: 'secondary.light'
              }}>
                <CardContent>
                  {/* Member Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>
                      <PersonIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {memberName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {updates.length} {updates.length === 1 ? 'update' : 'updates'}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Updates List */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {updates.slice(0, 3).map((update) => {
                      const categoryDetails = getCategoryDetails(update.category);

                      return (
                        <Box
                          key={update.id}
                          onClick={() => openViewDialog(update)}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'background.default',
                            '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: categoryDetails.color, width: 32, height: 32 }}>
                              {React.cloneElement(categoryDetails.icon, { fontSize: 'small' })}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip
                                  label={categoryDetails.label}
                                  size="small"
                                  sx={{
                                    bgcolor: categoryDetails.color,
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {format(parseISO(update.date), 'MMM d, yyyy')}
                                </Typography>
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {update.title}
                                {update.isEmergency && (
                                  <Chip label="EMERGENCY" size="small" color="error" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                )}
                              </Typography>
                              {update.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    mb: 0.5
                                  }}
                                >
                                  {update.description}
                                </Typography>
                              )}
                              {update.provider && (
                                <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                                  👨‍⚕️ Provider: {update.provider}
                                </Typography>
                              )}
                              {update.dosage && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  💊 {update.dosage} {update.frequency && `- ${update.frequency}`}
                                </Typography>
                              )}
                              {update.severity && update.category === 'allergy' && (
                                <Chip
                                  label={`Severity: ${update.severity}`}
                                  size="small"
                                  color={update.severity === 'life-threatening' || update.severity === 'severe' ? 'error' : 'warning'}
                                  sx={{ height: 18, fontSize: '0.65rem', mb: 0.5 }}
                                />
                              )}
                              {update.followUpDate && (
                                <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 0.5 }}>
                                  📅 Follow-up: {format(parseISO(update.followUpDate), 'MMM d, yyyy')}
                                </Typography>
                              )}
                              {update.location && (update.location.name || update.location.lat) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <LocationIcon sx={{ fontSize: '0.85rem', color: 'primary.main' }} />
                                  {update.location.lat ? (
                                    <Typography
                                      variant="caption"
                                      component="a"
                                      href={`https://maps.google.com/?q=${update.location.lat},${update.location.lng}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                                    >
                                      {update.location.name || 'View on Google Maps'}
                                      <MapIcon sx={{ fontSize: '0.75rem', ml: 0.3, verticalAlign: 'middle' }} />
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      {update.location.name}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {update.instructions && (
                                <Paper sx={{ p: 0.5, bgcolor: 'info.lighter', mt: 0.5, mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                    📋 {update.instructions}
                                  </Typography>
                                </Paper>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                                Added by {update.authorName}
                              </Typography>
                            </Box>
                            {update.authorId === currentUser.uid && (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(update); }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(update.id); }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}

                    {updates.length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                        + {updates.length - 3} more {updates.length - 3 === 1 ? 'update' : 'updates'}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Health Update Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUpdate ? 'Edit Health Update' : 'Add Health Update'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Family Member Name"
              value={formData.memberName}
              onChange={(e) => setFormData({ ...formData, memberName: e.target.value })}
              required
              placeholder="e.g., John, Mom, Grandpa"
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat.icon}
                      <Typography>{cat.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g., Annual checkup, New prescription"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              placeholder="Additional details..."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, description: formData.description + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />

            {/* Category-specific fields */}
            {(formData.category === 'doctor' || formData.category === 'appointment') && (
              <>
                <TextField
                  fullWidth
                  label="Provider/Doctor Name (Optional)"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  placeholder="Dr. Smith"
                />
                <TextField
                  fullWidth
                  label="Follow-up Date (Optional)"
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <LocationPicker
                  label="Location / Hospital (Optional)"
                  placeholder="Search for a hospital, clinic, or address…"
                  value={formData.location}
                  onChange={(location) => setFormData(prev => ({ ...prev, location }))}
                />
              </>
            )}

            {formData.category === 'medication' && (
              <>
                <TextField
                  fullWidth
                  label="Prescription Number (Optional)"
                  value={formData.prescriptionNumber}
                  onChange={(e) => setFormData({ ...formData, prescriptionNumber: e.target.value })}
                  placeholder="RX123456"
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Dosage (Optional)"
                      value={formData.dosage}
                      onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                      placeholder="10mg"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Frequency (Optional)"
                      value={formData.frequency}
                      onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                      placeholder="Twice daily"
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  label="Provider/Doctor Name (Optional)"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  placeholder="Dr. Smith"
                />
              </>
            )}

            {formData.category === 'allergy' && (
              <FormControl fullWidth>
                <InputLabel>Severity Level</InputLabel>
                <Select
                  value={formData.severity}
                  label="Severity Level"
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                >
                  <MenuItem value="mild">Mild</MenuItem>
                  <MenuItem value="moderate">Moderate</MenuItem>
                  <MenuItem value="severe">Severe</MenuItem>
                  <MenuItem value="life-threatening">Life-Threatening</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Instructions/Notes (Optional)"
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              multiline
              rows={2}
              placeholder="Additional medical instructions or notes..."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, instructions: formData.instructions + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            <FormControl fullWidth>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">Mark as emergency/critical?</Typography>
                <Select
                  value={formData.isEmergency}
                  onChange={(e) => setFormData({ ...formData, isEmergency: e.target.value })}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </Box>
            </FormControl>

            <Divider sx={{ my: 2 }}>
              <Chip label="Privacy & Tagging" size="small" />
            </Divider>

            {/* Privacy Control */}
            <PrivacyControl
              familyCode={familyBoardId}
              value={formData.privacy}
              onChange={(privacy) => setFormData({ ...formData, privacy })}
              label="Who can see this health update?"
            />

            {/* Tag Members */}
            <Box sx={{ mt: 2 }}>
              <TagMembers
                familyCode={familyBoardId}
                value={formData.taggedMembers}
                onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
                label="Tag Family Members"
                helperText="Tagged members will receive a notification about this health update"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingUpdate ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Health Update Dialog */}
      <Dialog open={viewDialogOpen} onClose={closeViewDialog} maxWidth="sm" fullWidth>
        {viewingUpdate && (() => {
          const cat = getCategoryDetails(viewingUpdate.category);
          return (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: cat.color, width: 36, height: 36 }}>
                    {React.cloneElement(cat.icon, { fontSize: 'small' })}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{viewingUpdate.title}</Typography>
                    <Chip label={cat.label} size="small" sx={{ bgcolor: cat.color, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                    {viewingUpdate.isEmergency && (
                      <Chip label="EMERGENCY" size="small" color="error" sx={{ ml: 0.5, fontWeight: 700 }} />
                    )}
                  </Box>
                </Box>
                <IconButton onClick={closeViewDialog}><CloseIcon /></IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Member: <strong>{viewingUpdate.memberName}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(parseISO(viewingUpdate.date), 'MMMM d, yyyy')}
                    </Typography>
                  </Box>

                  {viewingUpdate.description && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Description</Typography>
                      <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {viewingUpdate.description}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {viewingUpdate.provider && (
                    <Typography variant="body2">
                      👨‍⚕️ <strong>Provider:</strong> {viewingUpdate.provider}
                    </Typography>
                  )}

                  {viewingUpdate.dosage && (
                    <Typography variant="body2">
                      💊 <strong>Dosage:</strong> {viewingUpdate.dosage} {viewingUpdate.frequency && `— ${viewingUpdate.frequency}`}
                    </Typography>
                  )}

                  {viewingUpdate.prescriptionNumber && (
                    <Typography variant="body2">
                      📋 <strong>Prescription:</strong> {viewingUpdate.prescriptionNumber}
                    </Typography>
                  )}

                  {viewingUpdate.severity && viewingUpdate.category === 'allergy' && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Severity</Typography>
                      <Chip
                        label={viewingUpdate.severity}
                        color={viewingUpdate.severity === 'life-threatening' || viewingUpdate.severity === 'severe' ? 'error' : 'warning'}
                        size="small"
                      />
                    </Box>
                  )}

                  {viewingUpdate.followUpDate && (
                    <Typography variant="body2">
                      📅 <strong>Follow-up:</strong> {format(parseISO(viewingUpdate.followUpDate), 'MMMM d, yyyy')}
                    </Typography>
                  )}

                  {viewingUpdate.instructions && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Instructions / Notes</Typography>
                      <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {viewingUpdate.instructions}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {viewingUpdate.location && (viewingUpdate.location.name || viewingUpdate.location.lat) && (() => {
                    const loc = viewingUpdate.location;
                    const mapsUrl = buildMapsUrl(loc);
                    const dirsUrl = buildDirectionsUrl(loc);
                    const displayAddr = loc.address || loc.name || '';
                    return (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" /> Location
                        </Typography>
                        {loc.name && <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>{loc.name}</Typography>}
                        {displayAddr && loc.name !== displayAddr && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{displayAddr}</Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {mapsUrl && (
                            <Button size="small" variant="outlined" color="primary" startIcon={<MapIcon />}
                              component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer">
                              View
                            </Button>
                          )}
                          {dirsUrl && (
                            <Button size="small" variant="contained" color="primary" startIcon={<DirectionsIcon />}
                              component="a" href={dirsUrl} target="_blank" rel="noopener noreferrer">
                              Get Directions
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}

                  <Divider />
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Added by {viewingUpdate.authorName}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions>
                {viewingUpdate.authorId === currentUser.uid && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => {
                      closeViewDialog();
                      openEditDialog(viewingUpdate);
                    }}
                  >
                    Edit
                  </Button>
                )}
                <Button onClick={closeViewDialog}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

    </Container>
  );
};

export default Health;
